import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import { alpha } from '@mui/material/styles';
import Toolbar from '@mui/material/Toolbar';
import Tooltip from '@mui/material/Tooltip';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { FixedSizeList } from 'react-window';
import { HelpItem } from '../model/HelpItem';
import { KeywordsTranslator } from '../model/Translator';
import { useStyles } from '../styles';
import { buildUrl } from './UrlBuilder';
interface SidebarProps {
    open: boolean;
    helpItems: HelpItem[];
    isLargeScreen: boolean;
    apiVersion: string;
    translated: boolean;
}
interface FlattenHelpItem extends HelpItem {
    level: number;
}
const flattenTree = (nodes: HelpItem[], expandedNodes: Set<number>): FlattenHelpItem[] => {
    let flatList: FlattenHelpItem[] = [];
    const flattenHelper = (nodeList: HelpItem[], level: number) => {
        for (const node of nodeList) {
        flatList.push({ ...node, level });
        if (expandedNodes.has(node.id) && node.children) { // 如果当前节点是展开状态，则递归其子元素
            flattenHelper(node.children, level + 1);
        }
        }
    };
    flattenHelper(nodes, 0);
    return flatList;
};

const Row = ({
    index,
    style,
    expandedNodes,
    onExpandItem,
    onSelectItem,
    selectedItem,
    flatList,
    apiVersion,
    translated
}: {
    index: number;
    style?: React.CSSProperties;
    expandedNodes: Set<number>;
    onExpandItem: (id: number) => void;
    onSelectItem: (id: number) => void;
    selectedItem: number;
    flatList: FlattenHelpItem[];
    apiVersion: string;
    translated: boolean;
}) => {
    const data = flatList[index];
    const translatedTitle = translated && data? KeywordsTranslator.translateTitle(data.title) : data?.title;

    const classes = useStyles();
    // 创建一个 ref 来测量文本宽度
    const textRef = useRef<HTMLSpanElement | null>(null);
    const [isOverflow, setIsOverflow] = useState(false);

    useEffect(() => {
        if (textRef.current) {
            const element = textRef.current;
            setIsOverflow(element.scrollWidth > element.clientWidth);
        }
    }, [translatedTitle]);  
    if (data === undefined)
        return null;

  const isSelected = data.id === selectedItem;    
  return (
    <ListItem style={style} key={index} component="div" disablePadding 
      sx={{
        ...classes.listItem,
        // 自定义选中状态样式
        '& .MuiListItemButton-root.Mui-selected': {
          backgroundColor: (theme) => 
            alpha(theme.palette.primary.main, 0.15), // 选中背景色
          '&:hover': {
            backgroundColor: (theme) => 
              alpha(theme.palette.primary.main, 0.2), // 悬停时背景色
          }
        }
      }}>
      <ListItemButton 
        selected = {isSelected}          
        sx={{
          paddingLeft: `${data.level * 20}px`          
        }} 
        component = {Link}
        to={buildUrl(apiVersion, data.url)}
        onClick={() => onSelectItem(data.id)}
      >
        <ListItemIcon
          sx={classes.listItemIcon}
          onClick={(event) => {onExpandItem(data.id); event.preventDefault();}}
        >
          {
            data.children?.length ? (
              <IconButton size="small">
                {expandedNodes.has(data.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            ) : (
              <IconButton size="small" style={{ opacity: 0}}>
                <ExpandLessIcon />
              </IconButton>            
            )
          }
        </ListItemIcon>

  
        <Tooltip title={isOverflow ? translatedTitle : ''} arrow>
            <ListItemText
                primary={translatedTitle}
                primaryTypographyProps={{
                    noWrap: true, // 禁用换行
                    sx: {
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    color: (theme) => 
                        isSelected ? theme.palette.primary.main : 'inherit'
                    },
                    ref:textRef
                }}
            />
        </Tooltip>
      </ListItemButton>
    </ListItem>

  );
}

const useItemSize = () => {
  const [itemSize, setItemSize] = useState(0);
  const tempRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (tempRef.current) {
      const lineHeight = tempRef.current.getBoundingClientRect().height;
      setItemSize(lineHeight); // Set the calculated item size
    }
  }, []);

  return { itemSize, tempRef };
};
const Sidebar: React.FC<SidebarProps> = ({open, helpItems, isLargeScreen, apiVersion, translated}) => {
  const [expandedNodes, setExpandedNodes] = useState<Set<number>>(new Set([1])); 
  const [selectedItem, setSelectedItem] = useState(1);
  const [listHeight, setListHeight] = useState(600);
  const [flatList, setFlatList] = useState<FlattenHelpItem[]>(flattenTree(helpItems, expandedNodes));
  const [visibleItemCount, setVisibleItemCount] = useState(flatList.length);  
  const { itemSize, tempRef } = useItemSize();
  const toolRef = useRef<HTMLDivElement | null>(null);
  const listRef = useRef<FixedSizeList | null>(null);


    const classes = useStyles();
    const location = useLocation();


    useEffect(() => {
        const newFlatList = flattenTree(helpItems, expandedNodes);
        setFlatList(newFlatList);
        setVisibleItemCount(newFlatList.length);
    }, [helpItems, expandedNodes]);

    useEffect(() => {
        const handleResize = () => {
            if (toolRef.current) {
                setListHeight(window.innerHeight - toolRef.current.getBoundingClientRect().height);
            }
        };
        if (toolRef.current) {
            setListHeight(window.innerHeight - toolRef.current.getBoundingClientRect().height);
        }
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    // 同步选中态
    useEffect(() => {
        const url = location.pathname.split('/').pop();
        if (!url) {
            return;
        }
        const item = findItemWithParents(helpItems, url);
        if (!item) {
            return;
        }
        if (selectedItem !== item.node.id) {
            setSelectedItem(item.node.id);
            setExpandedNodes(pre => {
                const newExpandedNodes = new Set(pre);
                for (const parent of item.parents) {
                    newExpandedNodes.add(parent.id);
                }
                return newExpandedNodes;
            });
        }
        
    }, [location]);
    // 确保选中项可见
    useEffect(() => {
        const selectedItemIndex = flatList.findIndex(item => item.id === selectedItem);
        if (listRef.current && selectedItemIndex !== -1) {
            listRef.current.scrollToItem(selectedItemIndex);
        }
    }, [flatList, selectedItem]);
    // 切换节点展开状态
    const onExpandItem = (id: number) => {
        setExpandedNodes(prev =>
            prev.has(id)
                ? new Set([...Array.from(prev)].filter(nodeId => nodeId !== id))
                : new Set(prev).add(id)
            );    
    };
    const onSelectItem = (id: number) => {
        setSelectedItem(id);
    };
  
    return (
    <>
      {/* Temporary ListItem for computing height */}
      <ListItem key={-1} style={{ visibility: 'hidden', position: 'absolute' }}>
        <ListItemButton ref={tempRef} >
          <ListItemText primary={'Invisible text'}/>
        </ListItemButton>
      </ListItem>    
      <Drawer
        sx={{ ...classes.drawer}} 
        variant={isLargeScreen ? "persistent" : "temporary"}
        anchor="left"
        open={open}      
        PaperProps={{
          sx: {
            ...classes.drawerPaper,
            overflowY: 'auto',
          },
        }}
      >
        <Toolbar ref={toolRef} />
        <FixedSizeList ref={listRef} height={listHeight} itemCount={visibleItemCount} itemSize={itemSize} width="100%">
          {(props) => (
            <Row {...props} expandedNodes={expandedNodes} onExpandItem={onExpandItem} selectedItem={selectedItem} onSelectItem={onSelectItem}flatList={flatList} apiVersion={apiVersion} translated={translated}/>
          )}
        </FixedSizeList>
      </Drawer>
    </>);
};

interface FindItemResult {
    node: HelpItem; // 找到的节点
    parents: HelpItem[]; // 父节点路径
}

/**
 * 查找树结构中指定 id 的项，同时返回其父节点路径
 * @param tree - 树形结构的根节点（或数组）
 * @param id - 要查找的 id
 * @param parents - 当前节点的父节点路径（递归过程中维护）
 * @returns 找到的节点及其父节点路径；如果未找到返回 null
 */
function findItemWithParents(
    tree: HelpItem[],
    url: string,
    parents: HelpItem[] = []
): FindItemResult | null {
    for (const item of tree) {
        if (item.url === url) {
            return { node: item, parents }; // 找到目标节点，返回节点和父节点路径
        }

        if (item.children && item.children.length > 0) {
            const result = findItemWithParents(item.children, url, [...parents, item]); // 递归查找，并将当前节点加入父节点路径
            if (result) {
                return result; // 如果在子节点中找到，返回结果
            }
        }
    }

    return null; // 如果未找到，返回 null
}

export default Sidebar;